/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ReactNode } from 'react';

import { Code } from '@atlaskit/code';
import { cssMap, jsx } from '@atlaskit/css';
import { Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import CopyToClipboard from '../../copy-to-clipboard';

const styles = cssMap({
	button: {
		appearance: 'none',
		paddingInline: token('space.0'),
		paddingBlock: token('space.0'),
		backgroundColor: 'transparent',
		textAlign: 'left',
	},
});

type CodeCopyToClipboardProps = {
	/**
	 * The value to be copied to the clipboard.
	 */
	value: string;
	/**
	 * Messaging to display to the user.
	 */
	messages: {
		/**
		 * The message to display before copying.
		 */
		prompt: string;
		/**
		 * The message to display after successfully copying.
		 */
		success: string;
		/**
		 * The message to display if copying fails.
		 */
		fail: string;
	};
	/**
	 * The children to render.
	 */
	children: ReactNode;
};

/**
 * __Code copy to clipboard__
 *
 * A component that displays code which can be copied to the clipboard.
 */
const CodeCopyToClipboard: ({
	value,
	messages,
	children,
}: CodeCopyToClipboardProps) => JSX.Element = ({
	value,
	messages,
	children,
}: CodeCopyToClipboardProps) => {
	return (
		<CopyToClipboard value={value} messages={messages} resetMs={2000}>
			{({ copy }) => (
				<Pressable onClick={copy} xcss={styles.button}>
					<Code testId="code-copy">{children}</Code>
				</Pressable>
			)}
		</CopyToClipboard>
	);
};

export default CodeCopyToClipboard;
